/**
 * The Errors tile of the plan's facts strip (19/10/2026): today's errors
 * (red when one is critical, amber when one is open), the last one and its
 * kind, and whether every error reaches Slack. A click opens the Errors
 * tab.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { ShieldAlert } from 'lucide-react';

import { cn } from '@/lib/utils';

import { useErrorsReport } from '../../../hooks/useErrorQueries';
import { instantText } from '../../../utils/clock/dk-label';
import { errorFacts } from '../../errors-tab/errors-facts';
import { kindWords } from '../../errors-tab/errors-words';
import { useCountryClock } from '../../shared/time/country-clock-context';
import { FactTile } from '../schedule/FactTile';
import { errorsStatus } from './errors-fact';

const HINT =
  'Every error the automation hits today — a send SMSWiser refused, a day not laid out, texts not filled, a tracker read that failed, an alert not delivered — traced on the Errors tab with where, the message and what happened next. Every one goes to the country’s Slack thread while the Errors topic is on.';

export const ErrorsTile: FC<{ countryKey: string; at?: string | null; onOpen?: () => void }> = ({ countryKey, at = null, onOpen }) => {
  const report = useErrorsReport(countryKey, 1, at).data;
  const clock = useCountryClock();
  const facts = report ? errorFacts(report, report.rows, clock.tz) : null;
  const action = onOpen ? { label: 'Open the Errors tab', onClick: onOpen } : undefined;
  return (
    <FactTile
      icon={ShieldAlert}
      label="Errors"
      hint={HINT}
      action={action}
      detail={facts?.last ? `last ${instantText(facts.last.at, clock)} · ${kindWords(facts.last.kind).label}` : facts ? 'none today' : ' '}
      status={errorsStatus(facts, report?.slack ?? null)}
      testId="fact-errors"
    >
      <span className={cn(facts && facts.critical > 0 ? 'text-destructive' : facts && facts.open > 0 ? 'text-warning' : undefined)} data-testid="errors-fact">
        {facts ? facts.today : '—'}
      </span>
    </FactTile>
  );
};
